/**
 * The live call: what the phone and the server say to each other over
 * `GET /v1/agent/live`, a WebSocket that carries one speech-to-speech session.
 *
 * Used for setup (no business yet) and for create-with-voice inside a tenant
 * (`origin=new_record` or `new_list`). JSON text frames carry control; binary
 * frames carry audio. The phone sends PCM16 little-endian mono at
 * LIVE_INPUT_RATE; the server sends the model's voice back at LIVE_OUTPUT_RATE.
 * Words travel as `heard` and `said` fragments, and as one `turn` once the
 * note-taker has written: setup notes an understanding; create-with-voice
 * notes a partial `RecordNote` beside the same turn frame.
 *
 * docs/plans/realtime-setup-call.md, decisions 4 and 5.
 */
import type { AgentChatContext, AgentScreenCard, UnderstandingOutput } from './agent';
import type { AgentView } from './agent-view';
import type { AgentLanguageTag } from './agent-language';
import type { LiveCapabilities, LiveClientFrame, LiveDuplexMode, LiveEndReason, LiveSeat, LiveSensitivity, LiveServerFrame, LiveSetupSession } from './agent-live-wire';
import type { RecordBlankCounts, VisibleRecordKind } from './api';
import type { ValueTypeId } from './generated/value-types';

/**
 * The close code of a live call the server refused in the person's own
 * words (Talk live switched off in their role): the reason is that sentence.
 * Its own code, in the application range, so no other close is read as a
 * refusal: the server's 1008s carry machine words ('quota spent', 'send
 * start first', 'sign in first') that keep their own endings.
 */
export const LIVE_REFUSED_CLOSE = 4403;

/**
 * The close code of a live call another device of the same person took over
 * ("Continue live here"): the reason is the server's `agent.live_elsewhere`
 * sentence. Beside LIVE_REFUSED_CLOSE, in the application range.
 */
export const LIVE_ELSEWHERE_CLOSE = 4409;

/*
 * Live mode's clocks, shared so both clients draw the same countdowns
 * (docs/plans/os-voice-conversation.md, The conversation contract).
 */

/** No addressed activity this long: Still there? */
export const LIVE_IDLE_NUDGE_MS = 180_000;
/** No addressed activity this long: live mode ends. */
export const LIVE_IDLE_END_MS = 300_000;
/** The longest one live call runs before the roll-over. */
export const LIVE_CALL_CAP_MS = 1_800_000;
/** How long before the cap "Live mode stops in a minute · Carry on" shows. */
export const LIVE_ROLLOVER_WARN_MS = 60_000;
/** How long the server keeps a dropped call for its client to come back. */
export const LIVE_RECONNECT_GRACE_MS = 15_000;
/** The most speech a reconnecting client keeps and sends on. */
export const LIVE_RECONNECT_AUDIO_MS = 2_000;
/** The longest OS's first audio of an answer waits for the utterance's verdict. */
export const LIVE_VERDICT_HOLD_MS = 400;
/**
 * The end-of-speech wait per seat: everyday on the assistant, longer on the
 * setup. Phase 0 (G8) cut slow thinkers 7 of 7 at 1.2 s, recovered only by
 * full-duplex barge-in, and 0 of 2 at 2.0 s: the setup seat waits `setup` on
 * a full-duplex call and `setupHalf` on every other.
 */
export const LIVE_SILENCE_MS = { assistant: 600, setup: 1200, setupHalf: 2000 } as const;

/** PCM16 little-endian mono, phone to server. */
export const LIVE_INPUT_RATE = 16000;
/** PCM16 little-endian mono, server to phone. */
export const LIVE_OUTPUT_RATE = 24000;

/**
 * Why the call was opened. Absent on the wire (and on the query) means
 * `setup`, so today's phones keep working without a query string.
 */
export type LiveOrigin = 'setup' | 'new_record' | 'new_list';

/** One line of a typed conversation the call carries on from. */
export type LiveHistoryLine = {
  readonly role: 'user' | 'assistant';
  readonly text: string;
};

/**
 * What the phone knows when the call opens: the same setup context a chat
 * turn carries, and the thread so far when the person typed before they
 * spoke. `history` is what makes a switch from typing to the call seamless:
 * the model carries on from the last question instead of greeting again.
 * Origin travels on the URL (`?origin=`), not here: the route decides
 * tenant/owner refusals before the first frame arrives.
 */
export type LiveContext = Pick<AgentChatContext, 'language' | 'draft' | 'tz' | 'region' | 'localeCurrency' | 'beside'> & {
  readonly history?: readonly LiveHistoryLine[];
};

/**
 * What the note-taker writes on a record/list call: propose_record's input,
 * allowed to be half-filled until the speaking model calls `have_enough`.
 * `kind` is fixed by the call's origin; `counts` is null on a directory.
 */
export type RecordNote = {
  readonly kind: VisibleRecordKind;
  readonly name: string | null;
  readonly counts: RecordBlankCounts | null;
  readonly fields: ReadonlyArray<{
    readonly label: string;
    /** Null when the person named a field but not its type — the seat decides. */
    readonly type: ValueTypeId | null;
    readonly options?: readonly string[];
    /** The NAME of an existing list, never an id. */
    readonly targetList?: string;
  }>;
  readonly headline: string | null;
  /** Things the person said that no field holds. */
  readonly notes: readonly string[];
};

/** Slots the record/list note covers. A list never needs `counts`. */
export type RecordSlot = 'name' | 'counts' | 'fields';

export type RecordNoteOutput = {
  readonly kind: 'record_note';
  readonly note: RecordNote;
  readonly coverage: {
    readonly filled: readonly RecordSlot[];
    readonly missing: readonly RecordSlot[];
    readonly complete: boolean;
  };
  readonly ready: boolean;
};

/** Phone to server. `start` is always first; audio follows as binary frames. */
export type LiveClientMessage =
  | { readonly type: 'start'; readonly context: LiveContext }
  /** The mic tap. Muted, the phone sends no audio and the server tells the model nothing. */
  | { readonly type: 'mute'; readonly muted: boolean }
  /**
   * End the call. The voice goes at once; a question the assistant is still
   * answering, or small talk still being kept, is finished first, and its
   * `turn` arrives before the socket closes, so the conversation the person
   * moves to holds it.
   */
  | { readonly type: 'stop' }
  /**
   * A line the person did not speak: a tapped choice, or words typed in the
   * dock. The call takes it as the person's turn: it goes into the
   * transcript, the voice answers it, and the note-taker notes it, so its
   * `turn` carries it as `heard`. Only once the call is open; before `start`
   * it is refused like any other control frame.
   */
  | { readonly type: 'say'; readonly text: string }
  /**
   * Keep the call up and the voice quiet while the setup is being built or
   * read (B8): the server neither nudges nor closes a quiet call, and tells
   * the voice to stay silent. It ends on `release`, on the person's next
   * turn, or after LIVE_HOLD_MAX_MS. The call's own cap still stands.
   * Accepted any time after `start`: one sent while the voice is still
   * opening is kept, and the call opens on it, quiet, instead of greeting.
   */
  | { readonly type: 'hold'; readonly reason: LiveHoldReason }
  /** The hold is over: a quiet call is nudged, and then closed, again. */
  | { readonly type: 'release' }
  /**
   * The build landed: the setup as plain lines, in the card's order (records,
   * lists, links, on top), composed by the phone (B9). The voice says
   * `setup.call.here_it_is` in the call's language, reads the lines in order,
   * one sentence each, and asks whether to create it or change something; the
   * phone lights the row being read. Accepted any time after `start`; a newer
   * one replaces the last. Creating is always a tap, never a spoken yes.
   */
  | { readonly type: 'proposal'; readonly lines: readonly string[] }
  /**
   * Setup files the person added on the call, by id, once each is uploaded
   * (docs/plans/setup-from-files.md, Decided 9). The server reads their
   * summaries and tells the voice what each says as a note, which the
   * note-taker then reads with the rest; a file still being read is noted once
   * it is read. No image ever goes to the voice. Accepted any time after
   * `start`; an id already noted is not noted again. The setup's website
   * goes by id too, once the server holds it, read or not (Phase 9): the
   * voice is told it is being read, then what it gave or why it could not be
   * read, once each.
   */
  | { readonly type: 'files'; readonly fileIds: readonly string[] };

/** Why the phone holds the call: the build is running, or the person is reading what it made. */
export type LiveHoldReason = 'building' | 'reviewing';
export const LIVE_HOLD_REASONS: readonly LiveHoldReason[] = ['building', 'reviewing'];
export const isLiveHoldReason = (value: unknown): value is LiveHoldReason =>
  typeof value === 'string' && (LIVE_HOLD_REASONS as readonly string[]).includes(value);

/** The longest a hold lasts without a `release` or a turn: three minutes. */
export const LIVE_HOLD_MAX_MS = 3 * 60 * 1000;

export type LiveProviderId = 'google' | 'openai';

/** Server to phone. Audio is a binary frame, never one of these. */
export type LiveServerMessage =
  /** The provider's session is live; the greeting is on its way. */
  | { readonly type: 'open'; readonly provider: LiveProviderId }
  /** The first audio frame of a model turn is about to arrive. */
  | { readonly type: 'speaking' }
  /** The person cut in: drop every frame not yet played. */
  | { readonly type: 'interrupted' }
  /** What the person is saying, as the provider hears it. `final` closes the turn's words. */
  | { readonly type: 'heard'; readonly text: string; readonly final: boolean }
  /** What the model is saying, as the provider writes it. `final` closes the turn's words. */
  | { readonly type: 'said'; readonly text: string; readonly final: boolean }
  /**
   * One finished exchange, once the note-taker has written it down. Setup
   * fills `understanding`; create-with-voice fills `record` (and leaves
   * understanding null). Either note may be null when the taker failed; the
   * words still land. `ready` lights the next act (Build my setup / Draw it up).
   * A setup note's `understanding.ask` carries the choices the voice's line
   * offered (the line as the question, at most three options, no why), for the
   * phone to draw as chips under the voice's words; absent when it offered none.
   */
  | {
      readonly type: 'turn';
      readonly heard: string;
      readonly said: string;
      readonly understanding: UnderstandingOutput | null;
      readonly ready: boolean;
      readonly record?: RecordNoteOutput | null;
    }
  | { readonly type: 'error'; readonly code: string; readonly message: string }
  | { readonly type: 'closed'; readonly reason: string };

/* ------------------------------------------------ the assistant, live */

/**
 * What the phone knows when the assistant's live call opens, inside a
 * business: `GET /v1/agent/assistant/live`, with the session cookie AND the
 * tenant header. The same framing as the setup call — JSON control frames,
 * binary PCM16 little-endian mono at LIVE_INPUT_RATE up and LIVE_OUTPUT_RATE
 * down — over a different session: the speaking model is the assistant's
 * voice, and everything about the business's records goes through the
 * assistant itself (the chat agent), whose finished turns come back as thread
 * messages. docs/plans/done/orb-live-call.md.
 *
 * `threadId` and `messages` are the conversation the orb's sheet has open, as
 * the chat route takes them: the id every turn is kept under, and the thread
 * so far as UI messages (the shared package does not depend on the SDK, so
 * they travel as JSON the server validates and the phone casts).
 */
export type AssistantLiveContext = Pick<AgentChatContext, 'screen' | 'recordId' | 'tz' | 'cards' | 'language' | 'view'> & {
  readonly threadId: string;
  readonly messages: readonly unknown[];
  /**
   * True only on the first call a voice-first sheet starts; false on every
   * other start, a chat-first open and Talk live from the chat included (S9,
   * C109). The server reads it as `context.greet === true`, so a frame
   * without it never greets. The phone builds this context in `orb-sheet.tsx`.
   */
  readonly greet: boolean;
  /**
   * Set only on the call the landing opens, right after a setup's Create
   * (B12): the new business's name, the entry word of its first record, and
   * its first list's name, or null. The voice's greeting is then
   * `setup.coach.landed` with the name, in the call's language, instead of
   * the day's greeting, and it counts as the day's greeting. The phone draws
   * `entry` and `list` as the voice look's two chips.
   */
  readonly landed?: AssistantLanded;
  /** What a capable client can do. Absent: the call is legacy, and runs as today. */
  readonly capabilities?: LiveCapabilities;
  /** The seat the call opens on; absent is the assistant. */
  readonly seat?: LiveSeat;
  /**
   * The studio's setup session (a uuid), present only with `seat: 'setup'`:
   * the call opens the studio seat, where the drafter takes each line.
   * Absent is the legacy setup seat (onboarding-harness 7.2).
   */
  readonly setupSession?: LiveSetupSession;
  /** Come back to a dropped call inside its grace, with the `callId` and `token` its `open` gave. */
  readonly resume?: { readonly callId: string; readonly token: string };
  /** Take over the live call another device of the person's runs ("Continue live here"). */
  readonly takeover?: boolean;
  /** Live mode carries on after a setup's Create: one `setup_ready` line, no greeting. */
  readonly after?: 'create';
  /** How readily OS takes speech as its own, from Account (option B, step 6); absent is `normal`. */
  readonly sensitivity?: LiveSensitivity;
};

/** The business a setup just created, as the landing's call opens on it (B12). */
export type AssistantLanded = {
  readonly name: string;
  readonly entry: string;
  readonly list: string | null;
};

/**
 * How one of Coach's turns went, on a live call:
 *   answered  a reply with something to show: the sheet expands
 *   refused   the turn was refused before or by the model's fence: words only
 *   empty     Coach wrote nothing to show
 *   failed    the turn broke; what it had is in `messages`
 *   stopped   Stop cut it short; what it had is kept, marked stopped
 *   not_done  the turn set out to change something and drew nothing to
 *             confirm: the speech is a fixed line saying so, never a promise
 *             (L10 of docs/plans/links-fixes.md)
 */
export const ASSISTANT_TURN_OUTCOMES = ['answered', 'refused', 'empty', 'failed', 'stopped', 'not_done'] as const;
export type AssistantTurnOutcome = (typeof ASSISTANT_TURN_OUTCOMES)[number];
export const isAssistantTurnOutcome = (value: unknown): value is AssistantTurnOutcome =>
  typeof value === 'string' && (ASSISTANT_TURN_OUTCOMES as readonly string[]).includes(value);

/**
 * The look the call is on: the voice look, the dock, or minimised.
 * @deprecated Legacy wire; removed in step 6.2.
 */
export type AssistantLiveLook = 'voice' | 'dock' | 'minimised';

/*
 * Turn taking in live mode (docs/plans/os-live-mode.md, Decided 3 and 4). The
 * server owns the turn and tells the client with `mic`; the three windows are
 * one constant each, to be tuned later.
 */

/**
 * After the voice hands a question over, the mic stays open this long. A
 * second question inside it replaces the running one instead of waiting its
 * turn ("and October too"); after it, the mic is held while OS works.
 * @deprecated Legacy wire; removed in step 6.2.
 */
export const LIVE_ASK_WINDOW_MS = 1500;

/**
 * How long a client waits after a filter, a selection or a drawer changes
 * before it sends `place` (os-live-mode.md 2.3): a burst of clicks is one
 * place, the last.
 */
export const LIVE_PLACE_DEBOUNCE_MS = 300;
/**
 * After the voice's answer has played out (plus `LIVE_ECHO_GRACE_MS`), the mic
 * opens this long. If the person starts inside it, it stays open until they
 * finish; if not, live mode mutes it: the waiting state.
 * @deprecated Legacy wire; removed in step 6.2.
 */
export const LIVE_TURN_WINDOW_MS = 1500;
/**
 * The first window, after the greeting or the open, and after a click or tap to talk, is longer.
 * @deprecated Legacy wire; removed in step 6.2.
 */
export const LIVE_OPEN_WINDOW_MS = 6000;
/**
 * The mic stays shut this long after the voice's last sound has played, so
 * the speaker's tail is not heard as the person. The clients' own
 * `ECHO_GRACE_MS` is the same number.
 * @deprecated Legacy wire; removed in step 6.2.
 */
export const LIVE_ECHO_GRACE_MS = 300;

/**
 * Why live mode holds the mic, or does not:
 *   working   OS is answering a question: held
 *   speaking  the voice is speaking, or its sound is still playing: held
 *   waiting   the person did not start inside their window: held until a click or tap to talk
 *   open      the person's turn: the mic is heard
 * The person's own Mute is separate, and the client's alone.
 * @deprecated Legacy wire; removed in step 6.2.
 */
export const ASSISTANT_MIC_WHYS = ['working', 'speaking', 'waiting', 'open'] as const;
/** @deprecated Legacy wire; removed in step 6.2. */
export type AssistantMicWhy = (typeof ASSISTANT_MIC_WHYS)[number];
/** @deprecated Legacy wire; removed in step 6.2. */
export const isAssistantMicWhy = (value: unknown): value is AssistantMicWhy =>
  typeof value === 'string' && (ASSISTANT_MIC_WHYS as readonly string[]).includes(value);

/** What the voice heard the person say about a card: press it, leave it, or take it back. */
export type AssistantCardAction = 'confirm' | 'leave' | 'undo';

/** What pressing a card did, as the phone reports it back to the call. `not_in_view` comes only from an older client (R3: a voice yes presses a card wherever it is). */
export type AssistantCardResultState = 'done' | 'left' | 'undone' | 'failed' | 'needs_tap' | 'not_in_view' | 'not_found' | 'edit_first';

/** Phone to server. `start` is always first; audio follows as binary frames. */
export type AssistantLiveClientMessage =
  | { readonly type: 'start'; readonly context: AssistantLiveContext }
  /** Close or open the mic without ending the call. */
  | { readonly type: 'mute'; readonly muted: boolean }
  /**
   * End the call. The voice goes at once; a question the assistant is still
   * answering, or small talk still being kept, is finished first, and its
   * `turn` arrives before the socket closes, so the conversation the person
   * moves to holds it.
   */
  | { readonly type: 'stop' }
  /**
   * Stop: abort the question Coach is answering now, and only that. The call
   * goes on. What Coach had is kept, marked stopped. Accepted while the call
   * drains too (C42, C76). `messageId` names the question to stop: the running
   * one, or the queued one, which is then dropped before it runs. Absent:
   * whatever runs now.
   */
  | { readonly type: 'cancel'; readonly messageId?: string }
  /**
   * The person wants to talk: a click on the live button, or a tap on the orb
   * or the dock's mic, in any state. The server cuts the voice off (and drops
   * what it was still sending until it stops) and opens the mic (`mic` open).
   */
  | { readonly type: 'interrupt' }
  /**
   * Which look the call is on now. Sent after `start` whenever it changes; the call starts on `voice`.
   * @deprecated Legacy wire; removed in step 6.2.
   */
  | { readonly type: 'view'; readonly look: AssistantLiveLook }
  /**
   * A line for Coach that did not come by voice: typed, a chip, a dictation,
   * Ask again, or the follow-up after a confirm. Coach gets it word for word;
   * the voice is told the answer.
   */
  | { readonly type: 'say'; readonly text: string }
  /**
   * Where the person is now: sent on every screen change while the call is up
   * (minimised too), whenever the pinned cards change, and when the view
   * changes (a filter, a selection, a drawer), at most once per
   * `LIVE_PLACE_DEBOUNCE_MS`. The next Coach turn reads it; nothing is said.
   */
  | {
      readonly type: 'place';
      readonly screen: string;
      readonly recordId?: string;
      readonly cards?: readonly AgentScreenCard[];
      readonly view?: AgentView;
    }
  /** What pressing the card did, in the card's own words (Phase 11). */
  | { readonly type: 'card_result'; readonly toolCallId: string; readonly state: AssistantCardResultState; readonly receipt: string | null }
  /**
   * Files the person added on the call, by id, once each is read
   * (docs/plans/setup-from-files.md, Decided 19; board os-call-file), as the
   * setup call's `files`: the voice is told what each says, and the next
   * question OS answers carries them by reference. No image goes to the
   * voice. An id already sent is not sent again.
   */
  | { readonly type: 'files'; readonly fileIds: readonly string[] }
  /** Version 2, from a client that sent `capabilities` only (agent-live-wire.ts). */
  | LiveClientFrame;

/** Server to phone. Audio is a binary frame, never one of these. */
export type AssistantLiveServerMessage =
  /**
   * The provider's session is live: the mic is being heard. `greets` means the
   * voice is about to say the day's greeting, so the phone draws the greeting
   * look (Phase 6) and plays no earcon (Phase 5). A capable client also gets
   * the call's `duplex` (always), and the `callId` and `token` it resumes
   * with after a drop.
   */
  | {
      readonly type: 'open';
      readonly provider: LiveProviderId;
      readonly greets: boolean;
      readonly duplex?: LiveDuplexMode;
      readonly callId?: string;
      readonly token?: string;
    }
  /** The first audio frame of a model turn is about to arrive. */
  | { readonly type: 'speaking' }
  /** The person cut in: drop every frame not yet played. */
  | { readonly type: 'interrupted' }
  /**
   * Live mode's hold on the mic, the one source for the waiting state: held
   * while OS works (`working`) and while the voice speaks or its sound still
   * plays (`speaking`), open on the person's turn (`open`), and held again
   * when they did not start inside their window (`waiting`). Sent whenever it
   * changes. While held, the client sends no audio; the person's own Mute is
   * separate and the client's alone. A click or tap to talk (`interrupt`)
   * opens it in every state.
   * @deprecated Legacy wire; removed in step 6.2.
   */
  | { readonly type: 'mic'; readonly held: boolean; readonly why: AssistantMicWhy }
  /**
   * What the person is saying, as the voice hears it, fragment by fragment.
   * `final` closes the turn's words. These are the words the assistant is
   * asked, and they come only once the person has stopped: `hearing` is the
   * live line.
   * @deprecated Legacy wire; removed in step 6.2.
   */
  | { readonly type: 'heard'; readonly text: string; readonly final: boolean }
  /**
   * The person's words in the utterance being spoken, whole each time —
   * replace the line, never append. From a transcriber that runs beside the
   * voice, so they arrive while the person is still talking, about every half
   * second. A server without one sends none, and `heard` is all there is.
   * @deprecated Legacy wire; removed in step 6.2.
   */
  | { readonly type: 'hearing'; readonly text: string }
  /** What the voice is saying, as the provider writes it. `final` closes the turn's words. */
  | { readonly type: 'said'; readonly text: string; readonly final: boolean }
  /** The assistant is working on what was asked: reading the records, or drafting a change. */
  | { readonly type: 'working' }
  /**
   * The person's question, the moment it goes to Coach: draw their bubble
   * now. `messageId` is the id the thread keeps: the `turn` that answers it
   * carries the person's message under the same id, and replaces the bubble.
   * `queued` true: Coach is still on the question before; this one runs next,
   * and an `asking` with `queued` false and the same id says it has started.
   * A `turn` whose messages do not hold this id (a refusal before the line was
   * kept) removes the bubble.
   */
  | { readonly type: 'asking'; readonly messageId: string; readonly text: string; readonly queued: boolean }
  /**
   * One per tool Coach starts, in order: the tool's name as the model called
   * it and its input. A label, never a partial message: the phone words it
   * with `lib/agent-steps.ts` and shows the newest. Ids in `input` are for the
   * phone to name things by; never shown.
   */
  | { readonly type: 'progress'; readonly tool: string; readonly input: unknown }
  /**
   * The voice promised to look something up and called nothing (FD7: it
   * speaks as Coach, so the promise is its own). `heard` is the person's
   * question (Ask again sends it with `say`), `said` is what the voice said.
   * It comes whatever Coach runs; a line of the phone's still running goes on.
   * @deprecated Legacy wire; removed in step 6.2.
   */
  | { readonly type: 'not_reached'; readonly heard: string; readonly said: string }
  /** The voice heard a clear answer about a card: press it the way a tap would (Phase 11). */
  | { readonly type: 'confirm_card'; readonly toolCallId: string; readonly action: AssistantCardAction }
  /**
   * One finished exchange, as thread messages the phone appends: the person's
   * words and the assistant's reply, its cards included. They are already kept
   * under `threadId` on the server. `card` says the reply holds something to
   * confirm, which the voice cannot do for them.
   */
  | {
      readonly type: 'turn';
      readonly messages: readonly unknown[];
      readonly card: boolean;
      /**
       * How the turn went: answered (a reply with something to show: the sheet
       * expands), refused (refused before or by the model's fence: words only),
       * empty (Coach wrote nothing to show), failed (the turn broke; what it had
       * is in `messages`), stopped (Stop cut it short; what it had is kept,
       * marked stopped).
       */
      readonly outcome: AssistantTurnOutcome;
    }
  /**
   * The voice is gone. When Coach was still answering, its `turn` follows
   * before the socket closes: keep reading until the close.
   */
  | { readonly type: 'error'; readonly code: string; readonly message: string }
  /**
   * The voice is gone. When Coach was still answering, its `turn` follows
   * before the socket closes: keep reading until the close. `reason` is a
   * word no client shows (`idle`, `time`, `provider`, or the provider's own);
   * to a capable client it is a LiveEndReason (`isLiveEndReason`), and `ms`
   * is how long live mode ran, for the divider.
   */
  | { readonly type: 'closed'; readonly reason: string; readonly ms?: number }
  /** Version 2, to a client that sent `capabilities` only (agent-live-wire.ts). */
  | LiveServerFrame;

/** How a version-2 call ended, as its `closed` frame names it: the divider reads this. */
export type AssistantLiveEnding = { readonly reason: LiveEndReason; readonly ms?: number };

/* ------------------------------------------------------- dictation, live */

/**
 * The composer's dictation: `GET /v1/agent/dictate/live`, with the session
 * cookie AND the tenant header, like the assistant's call. A transcriber and
 * nothing else: no voice answers, nothing is asked of Coach, and nothing is
 * kept. JSON text frames carry control; binary frames carry the microphone,
 * PCM16 little-endian mono at LIVE_INPUT_RATE, about 100 ms a frame, as the
 * call's microphone sends. The phone draws the `words` that come back in its
 * own box while the person speaks; `finish` ends the recording, and `done`
 * carries the settled whole, which the phone sends as the person's turn.
 *
 * These are the dictation socket's own frames. Its `cancel` carries nothing
 * and is not the call's `cancel { messageId? }`, which travels on the
 * assistant call's socket; the two unions never share a parser.
 */

/** Phone to server. `start` is always first; audio follows as binary frames. */
export type DictationClientMessage =
  /** Always first. The transcriber's hint is the app's language. */
  | { readonly type: 'start'; readonly language: AgentLanguageTag }
  /** The check, a tap on the words, or the cap: no more audio. Settle and answer `done`. */
  | { readonly type: 'finish' }
  /** X: drop everything. No `done` follows. */
  | { readonly type: 'cancel' };

/**
 * Server to phone. `error` carries no `message`: the phone words it, and
 * nothing here is read by a person.
 */
export type DictationServerMessage =
  /** The transcriber took the setup: the words are being heard. */
  | { readonly type: 'open' }
  /**
   * The whole dictation so far: every settled line, then the utterance being
   * heard, joined by one space. Replace the box's words, never append.
   */
  | { readonly type: 'words'; readonly text: string }
  /** The settled whole after `finish`: the final text. The socket closes after it. */
  | { readonly type: 'done'; readonly text: string }
  /**
   * DICTATION_UNAVAILABLE: the transcriber would not open.
   * DICTATION_FAILED: it went away before the words settled.
   */
  | { readonly type: 'error'; readonly code: 'DICTATION_UNAVAILABLE' | 'DICTATION_FAILED' };

/** Where a sentence ends: the stops a voice's line closes on, Latin and Indic. */
const SENTENCE_END = /[.?!।॥]$/u;
/** Where the next one starts: a letter, in any script. */
const WORD_START = /^\p{L}/u;

/**
 * A live turn's words, put together from the provider's fragments. Inside a
 * sentence the fragments carry their own spaces and join as they came. Where
 * one fragment ends a sentence and the next begins a word with no space
 * between — two lines said in one turn, the question and then "I have what I
 * need…" — a space goes between them, so a bubble never reads "…to me?I have".
 * A digit after a stop is a number going on ("2." then "5"), never a new
 * sentence.
 */
export const joinSpoken = (parts: readonly string[]): string => {
  let words = '';
  for (const part of parts) {
    if (part === '') continue;
    words += SENTENCE_END.test(words) && WORD_START.test(part) ? ` ${part}` : part;
  }
  return words;
};
